import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, BookOpen } from 'lucide-react';
import axios from 'axios';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

export const ClusterPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCluster = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/clusters/${slug}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError('Cluster not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCluster();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6]">
        <p className="overline overline-gold mb-4">404</p>
        <h1 className="heading-editorial text-4xl mb-8">Cluster Not Found</h1>
        <Link to="/" className="btn-editorial">Return Home</Link>
      </div>
    );
  }

  const featured = data.articles[0];
  const rest = data.articles.slice(1);

  return (
    <div data-testid={`cluster-page-${slug}`} className="bg-[#FAF9F6]">
      <SEO
        title={`${data.name} — The Complete Guide`}
        description={data.description}
        image={data.image}
        url={`/clusters/${data.slug}`}
        type="website"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'Topics', url: '/' },
            { name: data.name, url: `/clusters/${data.slug}` },
          ],
        }}
      />

      {/* Cinematic Hero */}
      <section className="relative bg-[#111] text-white">
        <div className="absolute inset-0 z-0 opacity-50">
          <img src={data.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/70 to-[#111]/30" />
        </div>

        <div className="relative z-10 container-editorial pt-24 pb-20 lg:pt-32 lg:pb-28">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-white/60 mb-12">
            <Link to="/" className="hover:text-[#C69C6D] transition-colors">Home</Link>
            <span>/</span>
            <span>Topics</span>
            <span>/</span>
            <span className="text-white">{data.name}</span>
          </nav>

          <p className="overline text-[#C69C6D] mb-6">The Complete Hub</p>
          <h1 className="font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-white mb-8">
            {data.name}
          </h1>
          <p className="font-heading italic text-xl lg:text-3xl text-white/85 max-w-3xl mb-6">
            {data.tagline}
          </p>
          <p className="text-white/70 leading-relaxed max-w-3xl text-lg">{data.description}</p>
          <p className="overline text-white/50 mt-8">
            {data.article_count} {data.article_count === 1 ? 'article' : 'articles'} in this cluster
          </p>
        </div>
      </section>

      {/* Articles */}
      <section className="section-editorial">
        <div className="container-editorial">
          {data.articles.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-10 h-10 text-[#C69C6D] mx-auto mb-6" strokeWidth={1.2} />
              <p className="overline overline-gold mb-3">In Production</p>
              <h2 className="heading-editorial text-3xl text-[#111] mb-4">
                Articles Coming <span className="italic">Soon</span>
              </h2>
              <p className="text-[#555] max-w-md mx-auto">
                Our editorial team is researching this cluster. New articles publish daily as the
                content calendar advances.
              </p>
              <Link to="/blog" className="btn-editorial mt-8 inline-flex">
                Browse All Articles
              </Link>
            </div>
          ) : (
            <>
              {featured && (
                <div className="mb-20 pb-20 border-b border-[#E5E0D8]">
                  <div className="flex items-baseline justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
                    <p className="overline overline-gold">Editor's Pick</p>
                    <span className="text-xs text-[#888] tracking-wider">
                      {formatDate(featured.published_at)}
                    </span>
                  </div>

                  <Link
                    to={`/blog/${featured.slug}`}
                    className="group grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
                  >
                    <div className="lg:col-span-7 img-zoom aspect-[4/3] overflow-hidden">
                      <img
                        src={featured.image_url || data.image}
                        alt={featured.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="lg:col-span-5">
                      <p className="overline mb-4">{featured.category || 'Editorial'}</p>
                      <h2 className="font-heading text-3xl lg:text-5xl font-light leading-[1.1] tracking-tight text-[#111] mb-6 group-hover:text-[#C69C6D] transition-colors duration-500">
                        {featured.title}
                      </h2>
                      <p className="text-[#555] text-lg leading-relaxed mb-8">{featured.excerpt}</p>
                      <span className="inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#111] border-b border-[#111] pb-1">
                        Read Story
                        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                      </span>
                    </div>
                  </Link>
                </div>
              )}

              {rest.length > 0 && (
                <>
                  <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
                    <div>
                      <p className="overline overline-gold mb-3">The Archive</p>
                      <h2 className="heading-editorial text-4xl lg:text-5xl">All {data.name} Stories</h2>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                    {rest.map((post) => (
                      <Link
                        key={post.slug}
                        to={`/blog/${post.slug}`}
                        className="group block"
                        data-testid={`cluster-article-${post.slug}`}
                      >
                        <div className="aspect-[4/5] overflow-hidden img-zoom mb-6">
                          <img
                            src={post.image_url || data.image}
                            alt={post.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <p className="overline mb-3 flex items-center gap-3">
                          <span>{post.category || 'Editorial'}</span>
                          <span className="w-6 h-px bg-[#C69C6D]" />
                          <span className="text-[#888]">{formatDate(post.published_at)}</span>
                        </p>
                        <h3 className="font-heading text-2xl font-light leading-tight tracking-tight text-[#111] mb-3 group-hover:text-[#C69C6D] transition-colors duration-500">
                          {post.title}
                        </h3>
                        <p className="text-[#555] leading-relaxed line-clamp-2">{post.excerpt}</p>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {/* Back to Topics CTA */}
      <section className="bg-[#111] text-white py-16">
        <div className="container-editorial">
          <Link to="/" className="group inline-flex items-center gap-3 text-xs tracking-[0.15em] uppercase">
            <ArrowLeft className="w-4 h-4 text-[#C69C6D] group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
            Explore All Topics
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ClusterPage;
